import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { usePipecatClient } from '@pipecat-ai/client-react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { supabase } from '@/lib/supabase';
import { useGemBalance } from '@/hooks/useGemBalance';
import { CHAT_EVENTS, useTracking } from '@/hooks/useTracking';
import type { Database } from '@/types/database';

type Character = Database['eh']['Tables']['characters']['Row'];

export const GEMS_PER_MINUTE = 3;
export const MAX_CALL_DURATION_MINUTES = 120;

type CallState = 'idle' | 'pre-call' | 'requesting_url' | 'connecting' | 'connected' | 'ended' | 'error';
type EndReason = 'low_balance' | 'user_disconnected' | 'bot_disconnected' | 'error';

interface CallContextType {
  callState: CallState;
  forceOpenManager: boolean;
  preCall: (character: Character, chatId: string) => void;
  startCall: () => void;
  endCall: (reason: EndReason) => void;
  endReason: EndReason | null;
  character: Character | null;
  chatId: string | null;
  errorMessage: string;
  elapsedTime: number;
  initialBalance: number;
  showLowBalanceWarning: boolean;
  remainingCallTime: number;
  resetCall: () => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

export function CallProvider({ children }: { children: ReactNode }) {
  const { session } = useSessionContext();
  const { balance, refreshBalance } = useGemBalance();
  const { trackEvent } = useTracking();
  const client = usePipecatClient();
  const [character, setCharacter] = useState<Character | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  
  const [callState, setCallState] = useState<CallState>('idle');
  const [forceOpenManager, setForceOpenManager] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  // Optionally store transcripts/events later
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const [initialBalance, setInitialBalance] = useState(0);
  const [showLowBalanceWarning, setShowLowBalanceWarning] = useState(false);
  const [remainingCallTime, setRemainingCallTime] = useState(0);
  const [endReason, setEndReason] = useState<EndReason | null>(null);

  const endCallCallback = useCallback(async (elapsedTime: number, reason: EndReason | null = null) => {
    if (callStateRef.current !== 'ended') {
      setCallState('ended');
      if (reason) setEndReason(reason);
      trackEvent(CHAT_EVENTS.CHAT_CALL_ENDED, {
        characterId: character?.id,
        chatId,
        endReason: reason,
        elapsedMinutes: Math.round(elapsedTime / 60),
        userId: session?.user.id,
      });
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      await new Promise(resolve => setTimeout(resolve, 15000)); // wait for post-call webhook to complete
      await refreshBalance();
    }
  }, [character?.id, chatId, refreshBalance, session?.user.id, trackEvent]);

  const callStateRef = useRef(callState);
  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  // Error handling done inline where needed

  // Pipecat React client handles media; we'll manage state transitions here

  useEffect(() => {
    if (callState === 'connected') {
      callTimerRef.current = setInterval(() => {
        setElapsedTime(prevElapsedTime => {
          const newElapsedTime = prevElapsedTime + 1;
          const currentCost = (newElapsedTime / 60) * GEMS_PER_MINUTE;
          const remainingBalance = initialBalance - currentCost;

          if (import.meta.env.DEV && newElapsedTime % 10 === 0) console.log('CallContext.useEffect 1 second interval refresh', newElapsedTime, currentCost, remainingBalance);
          
          if (remainingBalance <= 5) {  
            setShowLowBalanceWarning(true);
            const remainingTimeInSeconds = Math.max(0, (remainingBalance / GEMS_PER_MINUTE) * 60);
            setRemainingCallTime(remainingTimeInSeconds);
          } else {
            setShowLowBalanceWarning(false);
          }

          if (remainingBalance <= 0) {
            try {
              client?.disconnect();
            } catch {}
            endCallCallback(elapsedTime, 'low_balance');
          }
          return newElapsedTime;
        });
      }, 1000);
    } else {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    }
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [callState, initialBalance, client]);

  const preCall = useCallback((char: Character, chId: string) => {
    const changedCharacters = char.id !== character?.id;
    if (['requesting_url', 'connecting', 'connected'].includes(callState)) {
      setForceOpenManager(true);
      if (changedCharacters) alert('Call already in progress. Please end the current call before starting a new one.');
      return;
    };
    setCharacter(char);
    setChatId(chId);
    setCallState('pre-call');
    setEndReason(null);
  }, [callState, character]);

  const startCall = useCallback(async () => {
    if (!character || !chatId || !session?.user) return;
    setInitialBalance(balance.total);
    setShowLowBalanceWarning(false);
    setElapsedTime(0);
    setCallState('requesting_url');
    try {
      const { data, error } = await supabase.functions.invoke('eh-start-call', {
        body: { characterId: character.id, chatId: chatId },
      });

      if (error) throw new Error(error.message);

      setCallState('connecting');

      if (!client) throw new Error('Pipecat client not initialized');
      await client.connect({ url: data.dailyRoom, token: data.dailyToken });

      client?.on('botDisconnected', () => {
        if (import.meta.env.DEV) console.log('[DEV] CallContext.startCall client bot disconnected');
        endCall('bot_disconnected');
      });

      setCallState('connected');
      trackEvent(CHAT_EVENTS.CHAT_CALL_STARTED, {
        characterId: character?.id,
        chatId,
        userId: session?.user.id,
      });
    } catch (err) {
      const error = err as Error;
      trackEvent(CHAT_EVENTS.CHAT_CALL_ERROR, {
        error: typeof error === 'string' ? error : JSON.stringify(error),
        characterId: character?.id,
        chatId,
        balance: balance.total,
        elapsedMinutes: 0,
        userId: session?.user.id,
      });
      setErrorMessage('An error occurred during the call. Please try again later.');
      setCallState('error');
    }
  }, [character, chatId, session, balance.total, client, trackEvent]);

  const endCall = useCallback((reason: EndReason = 'user_disconnected') => {
    try {
      client?.disconnect();
    } catch {}
    endCallCallback(elapsedTime, reason);
  }, [client, endCallCallback, elapsedTime]);
  
  const resetCall = useCallback(() => {
    setForceOpenManager(false);
    setCallState('idle');
    setElapsedTime(0);
    setErrorMessage('');
      // reset transcripts cache if used later
    setShowLowBalanceWarning(false);
    setEndReason(null);
    setCharacter(null);
    setChatId(null);
  }, []);


  const value = {
    callState,
    forceOpenManager,
    preCall,
    startCall,
    endCall,
    endReason,
    character,
    chatId,
    errorMessage,
    elapsedTime,
    initialBalance,
    showLowBalanceWarning,
    remainingCallTime,
    resetCall,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export const useCall = () => {
  const context = useContext(CallContext);
  if (context === undefined) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}; 